import * as api from '@/modules/store/_api';
import * as StoreConstants from '@/modules/store/_model/constants';
import BaseHelpers from '@/plugins/baseHelpers';
import StoreModel from '@/modules/store/_model';
import format from '@/plugins/baseFormat';
import Store from '@/models/Store';
import { storeFormat, } from '../_helpers/StoreFormat';

export default {
  namespaced: true,
  state: {
    cities: [],
    companyPlanURL: '',
    companyBasicInfoURL: '',
    companyMenuURL: '',
    companyDeliveryAreasURL: '',
    companyBanks: [],
    currentChecklist: {},
    currentStore: {},
    districts: [],
    paymentTypesSections: [],
    orders: [],
    assignedDrivers: [],
    bringgErrors: {
      phone: false,
      email: false,
    },
    basePlan: {},
    promoPlan: {},
    isLoading: false,
    financialStatus: {},
  },
  getters: {
    getCities ({ cities, },) {
      return cities;
    },
    getChecklist ({ currentChecklist, },) {
      return currentChecklist;
    },
    getCompanyBanks ({ companyBanks, },) {
      return companyBanks;
    },
    getCurrentStore ({ currentStore, },) {
      return currentStore;
    },
    getDistricts ({ districts, },) {
      return districts;
    },
    getBankAccountStatus ({ currentStore, }, { getNormalizedData, },) {
      return {
        value: currentStore?.bank_account?.status,
        label: getNormalizedData?.bank_account?.status,
        secondary: {
          value: currentStore?.secondary_bank_account?.status,
          label: getNormalizedData?.secondary_bank_account?.status,
        },
      };
    },
    getDocumentStatus ({ currentStore, },) {
      const type = currentStore?.document_type_current;
      const isCNPJPending = currentStore?.cnpj_validation?.status === 'PENDING';

      const ifCPF = type === 'CPF' ;
      const ifCNPJ = type === 'CNPJ';

      const data = ifCPF ?
        { ...currentStore.CPFStatus, } :
        { ...currentStore.CNPJStatus, };

      const isCPFandCNPJPending = ifCPF && isCNPJPending;
      const isCPFandCNPJNotPending = ifCPF && !isCNPJPending;

      const isCPF = isCPFandCNPJNotPending;
      const isCNPJ = ifCNPJ || isCPFandCNPJPending;

      const value = isCPFandCNPJPending ? 'PENDING' : data.value;
      const hasNotAlreadyBeenValidated = data.hasNotAlreadyBeenValidated;

      const { CNPJ_STATUS, } = StoreConstants;
      const label = isCPFandCNPJPending ? CNPJ_STATUS.PENDING : data.label;

      return {
        type,
        value,
        label,
        isCPF,
        isCNPJ,
        isCPFandCNPJPending,
        hasNotAlreadyBeenValidated,
      };
    },
    /* Normaliza os dados da loja com valores default e dados traduzidos  */
    getNormalizedData ({ currentStore, },) {
      const defaultedStore = {
        ...StoreModel,
        ...currentStore,
      };

      /* Atualiza os objetos aninhados */
      Object.keys(StoreModel,).forEach((key,) => {
        const isObject = BaseHelpers._toRawType(defaultedStore[key],) === 'Object';

        if (defaultedStore[key] && isObject) {
          defaultedStore[key] = {
            ...StoreModel[key],
            ...currentStore[key],
          };
        }
      },);

      return storeFormat.processStoreValues(defaultedStore,);
    },
    getPublishStatus ({ currentStore, }, { getNormalizedData, },) {
      return {
        value: currentStore.publish_status,
        label: getNormalizedData.publish_status,
      };
    },
    getValidationStatus ({ currentStore, }, { getNormalizedData, },) {
      return {
        value: currentStore?.bank_account?.status,
        label: getNormalizedData?.bank_account?.status,
      };
    },
    getOrders: (state,) => state.orders,
    getBringgErrors: (state,) => state.bringgErrors,
    getBasePlan ({ basePlan, },) {
      return basePlan;
    },
    getPromoPlans ({ promoPlan, },) {
      return promoPlan;
    },
    isLoading: (state,) => state.isLoading,
  },
  mutations: {
    setBankAccountStatus ({ currentStore, }, status,) {
      currentStore.bank_account = {
        ...currentStore.bank_account,
        status,
      };
    },
    setCities (state, data,) {
      state.cities = data;
    },
    setCurrentStore (state, data,) {
      state.currentStore = {
        ...data,
        CPFStatus: storeFormat.document(data, 'CPF',),
        CNPJStatus: storeFormat.document(data, 'CNPJ',),
      };
    },
    clearCurrentStore (state,) {
      state.currentStore = {};
    },
    setCompanyBanks: (state, banks,) => {
      state.companyBanks = banks;
    },
    setChecklist (
      state,
      {
        checklist,
        company_plan_url,
        company_basic_info_url,
        company_menu_url,
        company_delivery_areas_url,
        company_city_districts_url,
      },
    ) {
      state.currentChecklist = checklist;
      state.companyPlanURL = company_plan_url;
      state.companyBasicInfoURL = company_basic_info_url;
      state.companyMenuURL = company_menu_url;
      state.companyDeliveryAreasURL = company_delivery_areas_url;
      state.companyCityDistrictsURL = company_city_districts_url;
    },
    setDistricts (state, data,) {
      state.districts = data;
    },
    setPaymentTypesSections (state, paymentTypesSections,) {
      state.paymentTypesSections = paymentTypesSections;
    },
    setPublishStatus ({ currentStore, }, status,) {
      currentStore.publish_status = status;
    },
    setStoreOrders: (state, orders,) => {
      state.orders = new Store({orders: orders,},).orders;
    },
    setStoreAssignedDrivers: (state, assignedDrivers,) => {
      state.assignedDrivers = assignedDrivers.map((item,) => ({
        ...item,
        formatted_rating: item.average_rating ? (Math.floor(item.average_rating * 10,) / 10) : '-',
        formatted_last_order: format.dateFromNow(item.last_order,),
        formatted_status: (() => ({
          WAITING_APPROVAL: 'Aguardando aprovação',
          PENDING: 'Pendente',
          READY_FOR_TRAINING: 'Apto para treinamento',
          TRAINING: 'Em treinamento',
          READY_TO_DELIVER: 'Apto para realizar entrega',
          ACTIVE: 'Ativo',
          INACTIVE: 'Inativo',
          BLOCKED: 'Bloqueado',
          DISAPPROVED: 'Reprovado',
        }[item.status]))(),
      }),);
    },
    toogleSaving (state, data,) {
      state.isSaving = data;
    },
    toogleLoading (state, data,) {
      state.loading = data;
    },
    setBringgErrors (state, [field, value,],) {
      state.bringgErrors = {
        ...state.bringgErrors,
        [field]: value,
      };
    },
    setBasePlan (state, data,) {
      state.basePlan = data;
    },
    setPromoPlan (state, data,) {
      state.promoPlan = data;
    },
    setLoading: (state, isLoading,) =>
      (state.isLoading = isLoading),
    setFinancialStatus (state, data,) {
      state.financialStatus = data;
    },
  },
  actions: {
    fetchStore ({ commit, }, storeId,) {
      return api.fetchStore(storeId,).then(({ data, },) => {
        commit('setCurrentStore', data,);
        return data;
      },);
    },
    fetchAllStoreDetails ({commit,}, storeId,) {
      return Promise.all([
        api.getChecklist(storeId,),
        api.fetchStore(storeId,),
      ],).then(([checklist, store,],) => {
        commit('setChecklist', checklist.data,);
        commit('setCurrentStore', store.data,);
        return {
          checklist: checklist.data,
          store: store.data,
        };
      },);
    },
    fetchStoreOrdersList ({ commit, }, storeId,) {
      return api.getStoreOrders(storeId,).then(({ data, },) => {
        commit('setStoreOrders', data,);
        return data;
      },);
    },
    createStore ({ commit, }, formData,) {
      return api.createStore(formData,).
        then(({ data, },) => commit('setCurrentStore', {
          ...StoreModel,...data,
        },),).
        catch((error,) => Promise.reject(error.response.data.errors,),);
    },
    async patchStore ({ commit, }, [storeId, formData, fetchChecklist = false,],) {
      try {
        if (Object.prototype.hasOwnProperty.call(formData, 'image',)) {
          if (typeof formData.image !== 'string') {
            const payload = new FormData();
            payload.append('file', formData.image.file,);
            await api.updateStoreImage(storeId, payload,);
          }
          delete formData.image;
        }

        const { data: store, } = await api.updateStore(storeId, formData,);

        if (fetchChecklist) {
          const { data: checklist, } = await api.getChecklist(storeId,);
          commit('setChecklist', checklist,);
        }
        commit('setCurrentStore', store,);

      } catch (error) {
        return Promise.reject(error.response.data.errors,);
      }
    },
    async duplicateStore (_, [storeId, formData,],) {

      return await api.duplicateStore(storeId, formData,);

    },
    async fetchChecklist ({ commit, }, storeId,) {
      const checklist = await api.getChecklist(storeId,);
      commit('setChecklist', checklist.data,);
    },
    fetchCompanyBanks ({ commit, }, notAllowedBanks,) {

      return api.getCompanyBanks(notAllowedBanks,).
        then(({ data, },) => {
          const banks = data.map((bank,) => ({
            ...bank, text: `${bank.code} - ${bank.name}`,
          }),);

          commit('setCompanyBanks', banks,);
          return data;
        },);
    },
    /* Estado e cidade */
    fetchStatesAndCities ({ commit, }, franchiseId,) {
      return api.getStatesAndCities(franchiseId,).
        then(({ data, },) => commit('setCities', data.cities,),);
    },
    /* Bairros */
    fetchDistricts ({ commit, state, }, cityId,) {
      return api.getDistricts(cityId || state.currentStore?.address?.city_id,).
        then(({ data, },) => {
          commit('setDistricts', data,);
        },);
    },
    patchBankAccount ({ commit, state, }, [storeId, secondary_bank_account,],) {
      return api.patchBankAccount(storeId, secondary_bank_account,).
        then(() => commit('setCurrentStore', {
          ...state.currentStore,
          secondary_bank_account: {
            ...secondary_bank_account,
            status: 'WAITING_VALIDATION',
          },
        },),).
        catch((error,) => Promise.reject(error.response.data.errors,),);
    },
    validateBankAccount ({ state, commit, }, storeId,) {
      return api.validateBankAccount(storeId || state.currentStore._id,).
        then(() => commit('setBankAccountStatus', 'WAITING_VALIDATION',),).
        catch((error,) => Promise.reject(error.response.data.errors,),);
    },
    updateStoreStatusToSuspended ({ commit, dispatch, }, [storeId, reason,],){
      return api.updateStoreStatusToSuspended(storeId, reason,).
        then(() => {
          commit('setPublishStatus', 'SUSPENDED',);
          dispatch('fetchStore', storeId,);
        },).
        catch((error,) => Promise.reject(error.response.data.errors,),);
    },
    updateStoreStatusToClosed ({ commit, dispatch, }, [storeId, reason,],){
      return api.updateStoreStatusToClosed(storeId, reason,).
        then(() => {
          commit('setPublishStatus', 'CLOSED',);
          dispatch('fetchStore', storeId,);
        },).
        catch((error,) => Promise.reject(error.reposnse.data.erros,),);
    },
    updateStoreStatus ({ commit, }, [storeId, newStatus,],) {
      return api.publishStatus(storeId, newStatus,).
        then((status,) => commit('setPublishStatus',
          status.data.publish_status,),).
        catch((error,) => Promise.reject(error.response.data.errors,),);
    },
    updateBringgErrors ({ commit, }, [field, value,],) {
      commit('setBringgErrors', [field, value,],);
    },

    // Delivery Area
    postBasePlan ({ commit, }, storeId,) {
      return api.postBasePlan(storeId,).
        then(({ data, },) => {
          commit('setBasePlan', { ...data, },);
          return data;
        },).
        catch((error,) => Promise.reject(error.response.data.errors,),);
    },
    postPromoPlan ({ commit,}, [storeId, body,],) {
      return api.postPromoPlan(storeId, body,).
        then(({ data, },) => {
          commit('setPromoPlan', { ...data, },);
          return data;
        },).
        catch((error,) => Promise.reject(error.response.data.errors,),);
    },
    updateBasePlan ({ commit,}, [storeId, body,],) {
      return api.putBasePlan(storeId, body,).
        then(({ data, },) => {
          commit('setBasePlan', { ...data, },);
          return data;
        },).
        catch((error,) => Promise.reject(error.response.data.errors,),);
    },
    fetchBasePlan ({ commit, }, storeId,) {
      commit('setLoading', true,);
      return api.getBasePlan(storeId,).then(({ data, },) => {
        commit('setBasePlan', data,);
        commit('setLoading', false,);
        return data;
      },);
    },
    fetchPromoPlans ({ commit, }, storeId,) {
      commit('setLoading', true,);
      return api.getPromoPlans(storeId,).then(({ data, },) => {
        commit('setPromoPlan', data,);
        commit('setLoading', false,);
        return data;
      },);
    },
    deletePromoPlan ({ commit, }, [storeId, promoPlanId,],) {
      return api.deletePromoPlan(storeId, promoPlanId,).
        then(({ data, },) => commit('setPromoPlan', {
          ...data,
        },),).
        catch((error,) => Promise.reject(error.response.data.errors,),);
    },
    forceBasePlan ({ commit, }, storeId,) {
      return api.forceBasePlan(storeId,).
        then(({ data, },) => commit('setBasePlan', {
          ...data,
        },),).
        catch((error,) => Promise.reject(error.response.data.errors,),);
    },
    fetchDistrictsByStore ({ commit, }, storeId,) {
      return api.getDistrictsByStore(storeId,).then(({ data, },) => {
        commit('setDistricts', data,);
        return data;
      },);
    },
    financialStatus ({ commit, dispatch,}, [storeId, status,],) {
      return api.financialStatus(storeId, status,).
        then(() => {
          commit('setFinancialStatus', status,);
          dispatch('fetchStore', storeId,);
        },).
        catch((error,) => Promise.reject(error.response.data.errors,),);
    },

    // Store products
    getStoreProducts (_, data,) {
      return api.getStoreProducts(data,);
    },
    createStoreProduct (_, data,) {
      return api.createStoreProduct(data,);
    },
    disableStoreProduct (_, productId,) {
      return api.disableStoreProduct(productId,);
    },

    // Indexing kraken
    indexCompany (_, companyUuid,) {
      return api.indexCompany(companyUuid,);
    },
    deindexCompany (_, companyUuid,) {
      return api.deindexCompany(companyUuid,);
    },
  },
};
